import { useState, useCallback, useMemo } from 'react'
import { localStorageAdapter } from '@percent/utility'

function readPersisted(storageKey: string | undefined): string[] {
  if (!storageKey) return []

  try {
    const raw = localStorageAdapter.getItem(storageKey)
    const parsed = raw ? JSON.parse(raw) : []

    return Array.isArray(parsed)
      ? parsed.filter((id) => typeof id === 'string')
      : []
  } catch {
    return []
  }
}

export function useAccordionExpansion(itemIds: string[], storageKey?: string) {
  const [storedItems, setStoredItems] = useState<string[]>(() =>
    readPersisted(storageKey)
  )

  // Items arrive after the first render, so stale ids are dropped on read
  // rather than at mount, when there is nothing yet to match them against.
  const expandedItems = useMemo(
    () => storedItems.filter((id) => itemIds.includes(id)),
    [storedItems, itemIds]
  )

  const update = useCallback(
    (items: string[]) => {
      setStoredItems(items)

      if (storageKey) {
        localStorageAdapter.setItem(storageKey, JSON.stringify(items))
      }
    },
    [storageKey]
  )

  const isAllExpanded = useMemo(
    () =>
      itemIds.length > 0 && itemIds.every((id) => expandedItems.includes(id)),
    [itemIds, expandedItems]
  )

  const handleExpandAll = useCallback(() => {
    if (itemIds.length === 0) return

    update(isAllExpanded ? [] : itemIds)
  }, [itemIds, isAllExpanded, update])

  const handleAccordionChange = useCallback(
    (value: string | string[] | null) => {
      update(value === null ? [] : Array.isArray(value) ? value : [value])
    },
    [update]
  )

  return {
    expandedItems,
    handleExpandAll,
    handleAccordionChange,
    isAllExpanded,
  }
}
